<template>
  <VModal ref="dialog" disable-backdrop-click @close="$emit('close')">
    <template #default="modalProps">
      <div class="rounded shadow-xs w-full max-w-full h-full mx-auto">
        <div class="rounded shadow-lg bg-gray-100 overflow-hidden h-full flex flex-col">
          <div class="px-4 sm:px-6 py-5 border-b flex-none">
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <h1 class="text-sm">View Email Campaign</h1>
              </div>

              <div class="flex-none">
                <button
                  type="button"
                  class="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  @click="modalProps.close"
                >
                  <span class="sr-only">Close</span>
                  <svg
                    class="inline-flex h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div class="px-4 sm:px-6 py-4 border-b bg-white flex-none">
            <div>
              <div class="flex justify-between items-center">
                <nav class="flex space-x-4" aria-label="Tabs">
                  <a
                    v-for="option in tabOptions"
                    :key="option.value"
                    role="button"
                    :class="[
                      currentTab === option.value
                        ? 'bg-gray-100 text-gray-700 px-3 py-2 font-medium text-sm rounded-md'
                        : 'text-gray-500 hover:text-gray-700 px-3 py-2 font-medium text-sm rounded-md',
                    ]"
                    @click.prevent="currentTab = option.value"
                  >
                    {{ option.label }}
                  </a>
                </nav>
                <div v-if="emailCampaign" class="flex-none flex items-center gap-2">
                  <span>Status: {{ emailCampaign.status_display_name }}</span>
                  <BaseButton v-if="canCancel" danger small @click="cancelEmailCampaign">
                    Cancel
                  </BaseButton>
                  <BaseButton
                    v-if="canResendFailedMessages"
                    danger
                    small
                    @click="resendFailedMessages"
                  >
                    Resend Failed Messages
                  </BaseButton>
                </div>
              </div>
            </div>
          </div>
          <div class="flex-1 bg-white overflow-auto">
            <component
              v-if="emailCampaign"
              :is="currentComponent"
              :email-campaign="emailCampaign"
              :options="currentTabOptions"
              @switch-tab="handleSwitchTab"
            />
          </div>
        </div>
      </div>
    </template>
  </VModal>
</template>

<script>
import { emailCampaignResendFailedMessagesClient } from '@/api/clients';
import { emailCampaignsClient, accountActiveEmailCampaignsClient } from '@/api/clients';

export default {
  name: 'ViewEmailCampaignDialog',
  props: {
    emailCampaignId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      emailCampaign: null,
      tabOptions: {
        overview: {
          label: 'Overview',
          value: 'overview',
          component: () => import('./OverviewTab.vue'),
        },
        preview: {
          label: 'Preview',
          value: 'preview',
          component: () => import('./PreviewTab.vue'),
        },
        send_logs: {
          label: 'Send Logs',
          value: 'send_logs',
          component: () => import('./SendLogsTab.vue'),
        },
      },
      currentTab: 'overview',
      currentTabOptions: {},
      poll: null,
    };
  },
  computed: {
    currentComponent() {
      return this.tabOptions[this.currentTab]?.component ?? this.tabOptions.overview.component;
    },
    canCancel() {
      if (!this.emailCampaign) {
        return false;
      }

      return (
        ['DRAFT', 'CANCELLED', 'FAILED', 'COMPLETED'].indexOf(this.emailCampaign.status) === -1
      );
    },
    canResendFailedMessages() {
      if (this.emailCampaign?.status !== 'COMPLETED') {
        return false;
      }

      return Boolean(this.emailCampaign?.email_addresses_failed_count);
    },
  },
  created() {
    this.fetchEmailCampaign();
  },
  destroyed() {
    if (this.poll !== null) {
      clearTimeout(this.poll);
    }
  },
  methods: {
    fetchEmailCampaign() {
      return emailCampaignsClient
        .read(this.emailCampaignId, {
          include: [
            'email_message',
            'recipientsCount',
            'emailAddressesCount',
            'emailAddressesPendingCount',
            'emailAddressesSentCount',
            'emailAddressesFailedCount',
            'openCount',
            'uniqueOpenCount',
            'unsubscribedCount',
            'bouncedCount',
            'uniqueClickedCount',
            'clickedCount',
          ],
        })
        .then((response) => {
          this.emailCampaign = response.data;

          if (this.canCancel) {
            this.poll = setTimeout(() => {
              this.fetchEmailCampaign();
            }, 1000);
          }
        });
    },
    cancelEmailCampaign() {
      return accountActiveEmailCampaignsClient
        .destroy(this.emailCampaign.account_id, this.emailCampaign.id)
        .then(() => {
          this.fetchEmailCampaign();
        });
    },
    handleSwitchTab({ tab = 'overview', options = {} } = {}) {
      this.currentTab = tab;
      this.currentTabOptions = { ...options };
      this.$emit('switch-tab', { tab, options });
    },
    resendFailedMessages() {
      return emailCampaignResendFailedMessagesClient.store(this.emailCampaignId);
    },
  },
};
</script>
